import * as React from 'react';
import { concat, pathOr } from 'ramda';
import { isNotEmpty } from 'src/utilities/hasValue';
import { ActionItem } from './DropDownToggle';
import { mapIndexed } from 'src/utilities/visualFieldsMapper';
import Chevron from 'src/assets/icons/icon_chevron-down.svg';
import { Theme, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Link } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import NotificationLanding from './Notification/NotificationLanding';
import AdvisorProfilePopup from '../Accounts/Profile/AdvisorProfilePopup';
import { LOG_OUT } from "src/route_constants";

interface Props {
  userEmail: string;
  shortName: string;
  callback: string;
  accounts: any;
  advisorProfile: any;
}

const useAccounStyles = (open: boolean) =>
  makeStyles((theme: Theme) =>
    createStyles({
      account: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '13px',
      },
      chevron: {
        transform: open ? 'scaleY(-1)' : 'none',
        transition: 'all ease 0.3s',
      },
      menu: {
        boxShadow: 'none',
        top: 5,
        '& .MuiPaper-root': {
          boxShadow: 'none',
          border: `1px solid ${theme.palette.basic.lightGrey}`,
          '& .MuiMenuItem-root:hover': {
            background: theme.palette.purple.extraLight,
          },
        },
        '& .MuiTypography-root': {
          fontSize: 14,
          fontFamily: 'Open Sans',
        },
      },
    })
  );

const AdvisorMenu: React.FC<Props> = (props: Props) => {
  const { accounts, advisorProfile, callback, userEmail } = props;

  const [openProfile, setOpenProfile] = React.useState(false);

  const [actionItems, setActionItems] = React.useState<ActionItem[]>([]);
  const currentUrl = concat(pathOr('', ['location', 'pathname'], window), pathOr('', ['location', 'search'], window))
  React.useEffect(() => {
    const defaultActionItems: ActionItem[] = [
      {
        displayItem: <Typography>Profile</Typography>,
        onClick: () => {
          handleClose();
          setOpenProfile(true);
        },
        redirectUrl: currentUrl
      },
      {
        displayItem: <Typography>Logout</Typography>,
        onClick: () => {
          handleClose();
        },
        redirectUrl: LOG_OUT,
      },
    ];
    setActionItems(defaultActionItems);
  }, [callback]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const classes = useAccounStyles(open)();
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    /* @ts-ignore next-line */
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box display="flex">
      {isNotEmpty(accounts) && isNotEmpty(advisorProfile) && (
        <NotificationLanding
          accounts={accounts}
          advisorPersona={pathOr('', ['persona'], advisorProfile)}
        />
      )}
      <Box
        id="account-button"
        className={classes.account}
        aria-controls="account-button"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        {!matches && (
          <Typography ml={2} mr={1} fontSize={13}>
            {userEmail}
          </Typography>
        )}
        <Chevron
          className={classes.chevron}
          color={theme.palette.primaryColor}
        />
      </Box>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        classes={{
          root: classes.menu,
        }}
        elevation={0}
        autoFocus={false}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {mapIndexed((item: any, index: any) => {
          return (
            <MenuItem
              key={index}
              sx={{ width: '180px' }}
              component={Link}
              onClick={item.onClick ?? null}
              to={item.redirectUrl}
            >
              {item.displayItem}
            </MenuItem>
          );
        }, actionItems)}
      </Menu>

      <AdvisorProfilePopup
        openProfile={openProfile}
        setOpenProfile={() => setOpenProfile(false)}
      />
    </Box>
  );
};

export default AdvisorMenu;
